import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Link /* , Font */
} from '@react-pdf/renderer';
import MarkdownIt from 'markdown-it';
import PropTypes from 'prop-types';
import { Html } from 'react-pdf-html';

import { BUSINESS_ENTITY_ADVISOR_SECTIONS } from '#client/components/utils/constants';
import * as ROUTES from '#client/constants/routes';

const CLOUDINARY_BASE_URL = 'https://res.cloudinary.com/zenbusiness/q_auto/v1/shared-assets/s2';
const ZENBUSINESS_LOGO_URL =
  'https://res.cloudinary.com/zenbusiness/image/upload/v1749845000/shared-assets/s2/logo/zb-velo-compact-color.png';
const ICON_OVERVIEW_URL = `${CLOUDINARY_BASE_URL}/raster/`;
// Markdown parser (keep HTML disabled for safety; add plugins for GFM-like features)

const md = new MarkdownIt({
  html: false,
  breaks: true, // respect single line breaks
  linkify: true // auto-link URLs
});

const FOOTER_HEIGHT = 40;

const styles = StyleSheet.create({
  page: {
    padding: 32,
    paddingBottom: 32 + FOOTER_HEIGHT,
    fontSize: 12,
    lineHeight: 1.5,
    color: '#111'
  },
  header: {
    marginBottom: 24,
    borderBottom: '1pt solid #ccc',
    paddingBottom: 12
  },
  subheader: {
    fontSize: 10,
    color: 'grey',
    textTransform: 'uppercase',
    marginBottom: 8
  },
  title: { fontSize: 20, fontWeight: 'bold', marginBottom: 12 },
  subtitle: { fontSize: 12, color: '#444' },

  groupTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginTop: 10,
    marginBottom: 8
  },

  section: {
    marginBottom: 16,
    paddingBottom: 12,
    borderBottom: '1pt solid #eee',
    borderRadius: 4
  },
  sectionWhy: {
    backgroundColor: '#EAF2FF', // light blue
    borderRadius: 8,
    padding: '20px 24px 12px 24px', // padding bottom + 8px (<p> margin bottom)
    borderBottom: 0,
    marginBottom: 16
    // remove divider under this card
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 0
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#222'
  },
  sectionContent: {
    fontSize: 12,
    whiteSpace: 'pre-wrap'
  },
  subsectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginTop: 6,
    marginBottom: 4
  },
  subsectionWrapper: {
    marginTop: 8
  },
  subsectionBubble: {
    marginTop: 8,
    backgroundColor: '#E1EBFF',
    borderRadius: 8,
    padding: 12
  },
  tableWrapper: {
    border: '1pt solid #ddd',
    borderRadius: 4,
    overflow: 'hidden',
    padding: 0,
    marginTop: 6,
    marginBottom: 6
  },
  tableRow: {
    flexDirection: 'row',
    width: '100%'
  },
  tableHeaderRow: {
    flexDirection: 'row',
    width: '100%',
    backgroundColor: '#f5f5f5',
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    borderBottom: '1pt solid #ddd'
  },
  tableHeaderCellContainer: {
    backgroundColor: '#f5f5f5'
  },
  tableCellContainer: {
    flex: 1,
    minWidth: 0, // critical: allow content to shrink within flex row
    padding: 6,
    borderRight: '1pt solid #ddd'
  },
  tableCellLast: {
    borderRight: 0
  },
  tableCellText: {
    fontSize: 9,
    lineHeight: 1.25,
    color: '#333',
    flexShrink: 1
  },
  tableHeaderCellText: {
    fontWeight: 'bold',
    color: '#111'
  },
  statRow: {
    fontSize: 11,
    marginBottom: 4
  },
  highlightBox: {
    borderLeft: '3pt solid #2196f3',
    backgroundColor: '#e3f2fd',
    borderRadius: 4,
    padding: 8,
    marginTop: 6,
    marginBottom: 6
  },
  highlightTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 4
  },
  sectionIcon: {
    width: 20,
    height: 20,
    marginRight: 6
  },
  footerImage: {
    width: '100%'
    // height: 60,
    // marginRight: 6
  },

  footerImageIcon: {
    width: 180,
    height: 12
    // marginRight: 6
  },
  footer: {
    position: 'absolute',
    left: 32,
    right: 32,
    bottom: 24, // a little inset from page edge; change to 32 to align with padding
    height: FOOTER_HEIGHT,
    backgroundColor: '#fff' // keeps it clean if your viewer has a dark chrome
  },
  footerRule: {
    borderTop: '1pt solid #ddd',
    marginBottom: 6
  },
  footerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  footerLeft: {
    fontSize: 10,
    color: '#666'
  },
  footerRightImg: {
    width: 180, // your powered-by image width
    height: 15 // match the image’s aspect
  },
  // Optional: minimal CSS map for Html component (applies to nested HTML tags)
  htmlStyles: {
    // You can pass this object into <Html stylesheet={...} />
    'h1, h2, h3': { marginTop: 8, marginBottom: 6, fontWeight: 'bold' },
    h1: { fontSize: 18 },
    h2: { fontSize: 16 },
    h3: { fontSize: 14 },
    p: { marginBottom: 8 },
    ul: { marginBottom: 8, paddingLeft: 4, listStyleType: 'disc' },
    ol: { marginBottom: 8, paddingLeft: 4, listStyleType: 'disc' },
    li: { marginBottom: 4, listStyleType: 'disc' },
    a: { textDecoration: 'underline' },
    code: { fontSize: 11 },
    pre: {
      fontSize: 11,
      padding: 6,
      border: '1pt solid #ddd',
      borderRadius: 4,
      marginBottom: 8
    },
    blockquote: {
      borderLeft: '2pt solid #ccc',
      paddingLeft: 8,
      color: '#666',
      fontStyle: 'italic',
      marginBottom: 8
    },
    table: {
      width: '100%',
      border: '1pt solid #ddd',
      borderCollapse: 'collapse',
      marginBottom: 10
    },
    th: { padding: 4, fontWeight: 'bold', borderBottom: '1pt solid #ddd' },
    td: { padding: 4, borderBottom: '1pt solid #eee' },
    hr: { borderBottom: '1pt solid #ccc', marginTop: 8, marginBottom: 8 }
  }
});

// eslint-disable-next-line react/prop-types
function MarkdownPDFSection({ markdown }) {
  if (!markdown) return null;
  const html = md.render(markdown);
  return (
    <Html style={styles.sectionContent} stylesheet={styles.htmlStyles}>
      {html}
    </Html>
  );
}

function getCellValue(cell) {
  if (cell && typeof cell === 'object' && Object.prototype.hasOwnProperty.call(cell, 'value')) {
    return cell.value;
  }
  return cell;
}

function toCellText(cell) {
  const v = getCellValue(cell);
  if (v === null || typeof v === 'undefined') return '';
  return String(v);
}

function TablePDF({ content }) {
  if (!content || !content.headers || !content.rows) return null;
  const { headers, rows, caption } = content;
  const colCount = Array.isArray(headers) && headers.length > 0 ? headers.length : 1;

  return (
    <View style={styles.tableWrapper}>
      {/* Header */}
      <View style={styles.tableHeaderRow} wrap={false}>
        {(headers || []).map((header, colIdx) => {
          const isLast = colIdx === colCount - 1;
          return (
            <View
              key={`h-${toCellText(header)}`}
              style={{
                ...styles.tableCellContainer,
                ...styles.tableHeaderCellContainer,
                ...(isLast ? styles.tableCellLast : {})
              }}
            >
              <Text style={{ ...styles.tableCellText, ...styles.tableHeaderCellText }}>
                {toCellText(header)}
              </Text>
            </View>
          );
        })}
      </View>

      {/* Body */}
      {(rows || []).map((row, rowIdx) => (
        <View
          // eslint-disable-next-line react/no-array-index-key -- rows lack unique IDs in static PDF
          key={`r-${rowIdx}`}
          style={{
            ...styles.tableRow,
            borderBottom: rowIdx === rows.length - 1 ? '0pt solid transparent' : '1pt solid #eee'
          }}
          wrap={false}
        >
          {(row || []).map((cell, colIdx) => {
            const isLast = colIdx === colCount - 1;
            return (
              <View
                // eslint-disable-next-line react/no-array-index-key -- cells lack unique IDs in static PDF
                key={`c-${rowIdx}-${colIdx}`}
                style={{
                  ...styles.tableCellContainer,
                  ...(isLast ? styles.tableCellLast : {})
                }}
              >
                <Text style={styles.tableCellText}>{toCellText(cell)}</Text>
              </View>
            );
          })}
        </View>
      ))}

      {caption ? (
        <Text style={{ fontSize: 10, color: '#666', marginTop: 6 }}>{String(caption)}</Text>
      ) : null}
    </View>
  );
}

TablePDF.propTypes = {
  content: PropTypes.shape({
    headers: PropTypes.arrayOf(PropTypes.any).isRequired,
    rows: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.any)).isRequired,
    caption: PropTypes.string
  })
};

function StatCardsPDF({ content }) {
  const stats = content?.stats;
  if (!Array.isArray(stats) || stats.length === 0) return null;
  return (
    <View style={{ marginTop: 6, marginBottom: 6 }}>
      {stats.map((s) => (
        <Text key={s.label} style={styles.statRow}>
          {s.label}: {s.value}
        </Text>
      ))}
    </View>
  );
}

StatCardsPDF.propTypes = {
  content: PropTypes.shape({
    stats: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.any
      })
    )
  })
};

function HighlightPDF({ content }) {
  if (!content || !content.content) return null;
  return (
    <View style={styles.highlightBox}>
      {content.title ? <Text style={styles.highlightTitle}>{content.title}</Text> : null}
      <Text style={{ fontSize: 11, color: '#333' }}>{content.content}</Text>
    </View>
  );
}

HighlightPDF.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.string
  })
};

function renderContentBlock(block) {
  if (!block) return null;

  const component =
    (typeof block === 'object' && block.component) ||
    (typeof block === 'object' ? 'markdown' : 'markdown');
  const content =
    typeof block === 'object' && Object.prototype.hasOwnProperty.call(block, 'content')
      ? block.content
      : block;

  if (typeof content === 'string') {
    return <MarkdownPDFSection markdown={content} />;
  }

  if (component === 'table') {
    return <TablePDF content={content} />;
  }

  if (component === 'stat-cards') {
    return <StatCardsPDF content={content} />;
  }

  if (component === 'highlight') {
    return <HighlightPDF content={content} />;
  }

  try {
    return (
      <MarkdownPDFSection
        isBubble={block.isBubble}
        markdown={`\`\`\`\n${JSON.stringify(content, null, 2)}\n\`\`\``}
      />
    );
  } catch {
    return null;
  }
}

// Helper function to format section titles from camelCase or snake_case keys
const formatSectionTitle = (key) => {
  return String(key || '')
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/&/g, 'and')
    .replace(/\s+/g, ' ')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

const normalizeSections = (data) => {
  // Perform a deep copy to avoid mutating the input argument
  const result = JSON.parse(JSON.stringify(data));

  if (
    Array.isArray(data.items) &&
    data.items.length > 0 &&
    data.items.every(
      (item) =>
        item &&
        typeof item === 'object' &&
        item.sections &&
        typeof item.sections === 'object' &&
        !Array.isArray(item.sections)
    )
  ) {
    result.items = data.items.map((item) => {
      const sectionEntries = Object.entries(item.sections || {}).filter(([, value]) =>
        Boolean(value)
      );

      return {
        id: item.id || item.title,
        title: item.title || '',
        sections: sectionEntries.map(([key, value]) => ({
          id: `${key}`,
          title: value.title || formatSectionTitle(key),
          content:
            typeof value === 'string'
              ? value
              : // New grouped format uses `content` directly (string markdown or object for component)
                value.content,
          component: value.component || 'markdown',
          icon: value.icon,
          subsections: value.subsections || []
        }))
      };
    });
  }
  return result;
};

function mergeSubsectionForPDF(subsection, config) {
  if (!subsection) return null;
  return {
    ...subsection,
    title: subsection.title || config?.title,
    component: subsection.component || config?.component || 'markdown',
    content: subsection.content || config?.content,
    ...(config?.isBubble && { isBubble: config.isBubble }),
    ...(config?.disclaimer && { disclaimer: config.disclaimer }),
    ...(config?.showDisclaimer && { showDisclaimer: config.showDisclaimer })
  };
}

function mergeSectionForPDF(section, config) {
  if (!section) return null;

  const subsections = section.subsections
    ?.map((subsection) => {
      const subsectionConfig = config?.subsections?.find((s) => s.id === subsection.id);
      return mergeSubsectionForPDF(subsection, subsectionConfig);
    })
    .filter(Boolean);

  return {
    ...section,
    title: section.title || config?.title || formatSectionTitle(section.id),
    component: section.component || config?.component || 'markdown',
    content: section.content || config?.content,
    icon: section.icon || config?.icon,
    ...(config?.isBubble && { isBubble: config.isBubble }),
    ...(config?.disclaimer && { disclaimer: config.disclaimer }),
    ...(config?.showDisclaimer && { showDisclaimer: config.showDisclaimer }),
    ...(subsections && { subsections })
  };
}

export function BusinessEntityAdvisorResultPDF({ environment, report }) {
  const reportData = normalizeSections(report);
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Image src={ZENBUSINESS_LOGO_URL} style={{ width: 80, marginBottom: 20 }} />

        <View style={styles.header}>
          <Text style={styles.subheader}>Detailed business report</Text>
          <Text style={styles.title}>{reportData.title || 'Business Entity Advisor Report'}</Text>
          {reportData.description ? (
            <Text style={styles.subtitle}>{reportData.description}</Text>
          ) : null}
        </View>
        {/* Grouped items -> subtitle + sections */}
        {BUSINESS_ENTITY_ADVISOR_SECTIONS.map((sectionConfig) => {
          const sectionData = reportData.items.find((item) => item.id === sectionConfig.id);
          if (!sectionData) return null;

          return (
            <View key={sectionData.id || sectionData.title}>
              {sectionData.title ? (
                <Text style={styles.groupTitle}>{sectionData.title}</Text>
              ) : null}

              {sectionConfig.subsections
                .map((subConfig) => {
                  const section = sectionData.sections.find((s) => s.id === subConfig.id);
                  return mergeSectionForPDF(section, subConfig);
                })
                .filter(Boolean)
                .map((section) => {
                  return (
                    <View key={section.id} style={styles.section}>
                      <View style={styles.sectionHeader} wrap={false}>
                        {section.icon && (
                          <Image
                            src={`${ICON_OVERVIEW_URL}${section.icon}`}
                            style={styles.sectionIcon}
                          />
                        )}
                        <Text style={styles.sectionTitle}>{section.title}</Text>
                      </View>

                      {renderContentBlock({
                        component: section.component,
                        content: section.content
                      })}

                      {(section.subsections || []).map((sub, idx) => (
                        <View
                          key={sub.id || idx}
                          style={sub.isBubble ? styles.subsectionBubble : styles.subsectionWrapper}
                        >
                          {sub.title ? (
                            <Text style={styles.subsectionTitle}>{sub.title}</Text>
                          ) : null}
                          {renderContentBlock(sub)}
                        </View>
                      ))}
                    </View>
                  );
                })}
            </View>
          );
        })}

        <Link href={ROUTES.website(environment)} style={styles.footerImage} target="_blank">
          <Image
            src={`${CLOUDINARY_BASE_URL}/raster/business-ideas-banner.png`}
            style={styles.footerImage}
          />
        </Link>

        <View fixed style={styles.footer}>
          <View style={styles.footerRule} />

          <View style={styles.footerRow}>
            <Text style={styles.footerLeft}>support@zenbusiness.com</Text>
            <Image
              src={`${CLOUDINARY_BASE_URL}/raster/powered-by-zb-velo.png`}
              style={styles.footerRightImg}
            />
          </View>
        </View>
      </Page>
    </Document>
  );
}

BusinessEntityAdvisorResultPDF.propTypes = {
  environment: PropTypes.string,
  report: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        sections: PropTypes.object
      })
    )
  }).isRequired
};

export default BusinessEntityAdvisorResultPDF;
